import _ from 'lodash';

import {
  CycleMode,
  DEFAULT_CERTIFICATION_CYCLE,
  FIXED_DAYS_DEFAULT,
  REMINDERS_INVALID_MESSAGES,
  RemindersInvalidReason,
  RENEWAL_WINDOW_DEFAULT,
} from './CertificationCycleSection.constants';

const POSITIVE_INTEGER_PATTERN = /^\d+$/;

export const parseRemindersText = text => {
  const trimmed = (text || '').trim();

  if (!trimmed) {
    return { isValid: false, values: [], reason: RemindersInvalidReason.EMPTY };
  }

  const parts = trimmed.split(',').map(part => part.trim());

  const hasNonPositiveInteger = parts.some(part => !POSITIVE_INTEGER_PATTERN.test(part));

  if (hasNonPositiveInteger) {
    return {
      isValid: false,
      values: [],
      reason: RemindersInvalidReason.NON_POSITIVE_INTEGER,
    };
  }

  const values = parts.map(Number);

  const hasZero = values.some(value => value <= 0);

  if (hasZero) {
    return {
      isValid: false,
      values,
      reason: RemindersInvalidReason.NON_POSITIVE_INTEGER,
    };
  }

  const isStrictlyDecreasing = values.every(
    (value, index) => index === 0 || value < values[index - 1],
  );

  if (!isStrictlyDecreasing) {
    return {
      isValid: false,
      values,
      reason: RemindersInvalidReason.NOT_STRICTLY_DECREASING,
    };
  }

  return { isValid: true, values, reason: null };
};

export const getRemindersValidationMessage = text => {
  const { isValid, reason } = parseRemindersText(text);

  if (isValid) {
    return '';
  }

  return REMINDERS_INVALID_MESSAGES[reason] || '';
};

export const formatRemindersValuesToText = values =>
  Array.isArray(values) && values.length ? values.join(', ') : '';

export const getInitialCertificationCycleFromSettings = (parsedSettings = {}) => {
  const cyclePeriod = parsedSettings.certificationCyclePeriod || null;
  const reminders = parsedSettings.recertificationReminders || null;
  const isKnownMode = Object.values(CycleMode).includes(cyclePeriod?.mode);

  return {
    cycleMode: isKnownMode ? cyclePeriod.mode : DEFAULT_CERTIFICATION_CYCLE.cycleMode,
    cycleFixedDays: _.isNil(cyclePeriod?.fixedDays) ? FIXED_DAYS_DEFAULT : cyclePeriod.fixedDays,
    recertificationRenewalWindowDays: _.isNil(parsedSettings.recertificationRenewalWindowDays)
      ? RENEWAL_WINDOW_DEFAULT
      : parsedSettings.recertificationRenewalWindowDays,
    remindersEnabled: !_.isNil(reminders),
    remindersDaysBeforeExpiry: formatRemindersValuesToText(reminders?.daysBeforeExpiry),
  };
};

export const buildCertificationCyclePayload = form => {
  const cycleMode = form.cycleMode || DEFAULT_CERTIFICATION_CYCLE.cycleMode;
  const isFixedDays = cycleMode === CycleMode.FIXED_DAYS;

  const fixedDays = isFixedDays ? Number(form.cycleFixedDays) || FIXED_DAYS_DEFAULT : null;

  const remindersParseResult = form.remindersEnabled
    ? parseRemindersText(form.remindersDaysBeforeExpiry)
    : null;

  const recertificationReminders = remindersParseResult?.isValid
    ? { daysBeforeExpiry: remindersParseResult.values }
    : null;

  const renewalWindowRaw = Number(form.recertificationRenewalWindowDays);
  const recertificationRenewalWindowDays = Number.isFinite(renewalWindowRaw)
    ? renewalWindowRaw
    : RENEWAL_WINDOW_DEFAULT;

  return {
    certificationCyclePeriod: {
      mode: cycleMode,
      fixedDays,
    },
    recertificationRenewalWindowDays,
    recertificationReminders,
  };
};
